// ./src/components/features/Dashboard/DashboardWelcome/DashboardWelcome.tsx
import React from "react";
import { Paper, Title, Text, Stack, SimpleGrid, ThemeIcon } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppIcon } from "@/components/generic/AppIcon";

export interface DashboardWelcomeProps {
  /** User's first name for personalized greeting */
  userName?: string | null;
}

/**
 * Welcome message shown to new users who have no vehicles yet.
 * Briefly explains what the app does and how to get started.
 */
export const DashboardWelcome: React.FC<DashboardWelcomeProps> = ({ userName }) => {
  const { t } = useTranslation(["common"]);

  const greeting = userName
    ? t("common:dashboard.welcome.greetingName", { name: userName })
    : t("common:dashboard.welcome.greeting");

  return (
    <Paper p="xl" radius="md" withBorder>
      <Stack gap="md">
        <Title order={2}>{greeting}</Title>
        <Text c="dimmed" size="lg">
          {t("common:dashboard.welcome.subtitle")}
        </Text>

        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mt="sm">
          <WelcomeFeature
            icon="Car"
            titleKey="common:dashboard.welcome.features.vehicles.title"
            descKey="common:dashboard.welcome.features.vehicles.description"
          />
          <WelcomeFeature
            icon="GasStation"
            titleKey="common:dashboard.welcome.features.refuels.title"
            descKey="common:dashboard.welcome.features.refuels.description"
          />
          <WelcomeFeature
            icon="ReportMoney"
            titleKey="common:dashboard.welcome.features.expenses.title"
            descKey="common:dashboard.welcome.features.expenses.description"
          />
          <WelcomeFeature
            icon="ChartLine"
            titleKey="common:dashboard.welcome.features.reports.title"
            descKey="common:dashboard.welcome.features.reports.description"
          />
        </SimpleGrid>

        <Text c="dimmed" size="sm" mt="xs">
          {t("common:dashboard.welcome.getStarted")}
        </Text>
      </Stack>
    </Paper>
  );
};

// ============================================
// Internal sub-component
// ============================================

interface WelcomeFeatureProps {
  icon: string;
  titleKey: string;
  descKey: string;
}

const WelcomeFeature: React.FC<WelcomeFeatureProps> = ({ icon, titleKey, descKey }) => {
  const { t } = useTranslation();

  return (
    <Stack gap={4}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ThemeIcon variant="light" size="md" radius="md">
          <AppIcon iconName={icon} size={16} />
        </ThemeIcon>
        <Text fw={600} size="sm">{t(titleKey)}</Text>
      </div>
      <Text c="dimmed" size="sm" pl={40}>
        {t(descKey)}
      </Text>
    </Stack>
  );
};